import React, { useState, useEffect } from 'react';
import { Modal, Button, Alert, Card, Form, Badge, Spinner, ProgressBar } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import Select from 'react-select';
import { brandSelectTheme } from '../Common/reactSelectTheme';
import BankTransactionFacts from './BankTransactionFacts';
import BookingEditor from './booking/BookingEditor';
import useBookingDraft from '../../../hooks/useBookingDraft';
import { bookingSuggestionTitle } from '../../../services/BookingProposalService';
import { roleSettingsPath } from '../../../services/api/chartRolesApi';
import {
  CheckCircle,
  AlertCircle,
  FileText,
  TrendingUp,
  TrendingDown,
  ChevronLeft,
  Lightbulb,
  Edit3,
} from 'lucide-react';

/**
 * Two-stage flow for creating a verification from a known transaction.
 * Stage 1 chooses the source of the posting: automatic proposal, template,
 * or fully manual. Choosing a ready proposal advances immediately.
 * Stage 2 is the shared BookingEditor with the booking action in the footer.
 *
 * The bank sheet books inline (BankhandelserPage → BookingEditor) and no
 * longer opens this modal; the Skatteverket page still does. All state and
 * the POST live in useBookingDraft, shared with the sheet.
 *
 * Optional `initialRows` prop ([{ account_number, account_name, debit,
 * credit }, ...], min 2 rows): prefills the row editor and skips straight
 * to step 2.
 */
const CreateVerificationFromTransactionModal = ({ show, onHide, transaction, onSuccess, initialRows = null, bankLeg = null }) => {
  const { companyId } = useParams();
  const draft = useBookingDraft({ companyId, transaction, bankLeg, enabled: show });

  const [currentStep, setCurrentStep] = useState(1);
  const [discoveryMode, setDiscoveryMode] = useState('suggestions');
  const [templateSearch, setTemplateSearch] = useState('');

  const hasInitialRows = Boolean(initialRows && initialRows.length >= 2);
  useEffect(() => {
    if (!show || !transaction) return;
    setDiscoveryMode('suggestions');
    setTemplateSearch('');
    draft.init({ initialRows: hasInitialRows ? initialRows : null });
    setCurrentStep(hasInitialRows ? 2 : 1);
    // draft.init is stable per transaction; re-running on every render would refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, transaction]);

  const handleSuggestionSelect = (suggestion) => {
    draft.selectSuggestion(suggestion);
    setCurrentStep(2);
  };
  const handleSkipSuggestions = () => {
    draft.startManualRows();
    setCurrentStep(2);
  };
  const handleApplyTemplate = async () => {
    if (await draft.applyTemplate()) setCurrentStep(2);
  };
  const handleBack = () => {
    setCurrentStep(currentStep - 1);
    draft.setError(null);
  };
  const handleCreateVerification = async () => {
    const data = await draft.submit();
    if (data) {
      onSuccess?.(data);
      onHide();
    }
  };

  const getConfidenceBadgeColor = (confidence) => {
    if (!Number.isFinite(confidence)) return 'secondary';
    if (confidence >= 0.9) return 'success';
    if (confidence >= 0.7) return 'info';
    return 'warning';
  };
  const getConfidenceLabel = (suggestion) => {
    const tier = suggestion.confidence_tier;
    if (tier === 'high') return 'Hög träffsäkerhet';
    if (tier === 'medium') return 'Medel träffsäkerhet';
    if (tier === 'low') return 'Behöver granskas';
    return 'Granska förslaget';
  };
  const formatCurrency = (amount) => new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(amount);
  const evidenceLabel = (item) => (typeof item === 'string' ? item : (item?.label || item?.reason || item?.description || null));
  const warningLabel = (item) => (typeof item === 'string' ? item : (item?.message || item?.error || item?.label || null));

  if (!transaction) return null;

  const isIncoming = parseFloat(transaction.amount) > 0;
  const visibleTemplates = draft.templates.filter((template) => {
    const query = templateSearch.trim().toLowerCase();
    if (!query) return true;
    return `${template.template_name || ''} ${template.description || ''}`.toLowerCase().includes(query);
  });
  // With caller-provided rows the wizard starts at step 2 — "Tillbaka"
  // from there is a cancel, not a trip to the empty suggestion step.
  const startStep = hasInitialRows ? 2 : 1;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header className="border-0 pb-0">
        <Modal.Title className="d-flex align-items-center gap-2">
          <div
            style={{
              width: '40px', height: '40px', borderRadius: '10px',
              backgroundColor: '#eff6ff', color: '#3b82f6',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <FileText size={20} />
          </div>
          <div>
            <span style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1f2937' }}>
              Bokför transaktion
            </span>
            <div style={{ fontSize: '0.875rem', fontWeight: '400', color: '#6b7280' }}>
              Steg {currentStep} av 2
            </div>
          </div>
        </Modal.Title>
        <button type="button" className="btn-close" onClick={onHide} aria-label="Stäng" />
      </Modal.Header>

      <Modal.Body className="px-4 py-4">
        <ProgressBar className="mb-4" style={{ height: '6px' }}>
          <ProgressBar variant="primary" now={(currentStep / 2) * 100} />
        </ProgressBar>

        {/* Transaction Info Summary */}
        <Card className="mb-4" style={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb' }}>
          <Card.Body className="p-3">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '4px' }}>Transaktion</div>
                {draft.transactionType === 'skatteverket'
                  ? <div style={{ fontSize: '0.875rem', fontWeight: '500', color: '#1f2937' }}>{transaction.description}</div>
                  : <BankTransactionFacts line={transaction} variant="summary" />}
              </div>
              <div className="text-end">
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '4px' }}>Belopp</div>
                <div style={{ fontSize: '1.125rem', fontWeight: '600', color: isIncoming ? '#16a34a' : '#dc2626' }}>
                  {isIncoming ? <TrendingUp size={16} className="me-1" /> : <TrendingDown size={16} className="me-1" />}
                  {formatCurrency(transaction.amount)}
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* Step 1 shows the shared alerts itself; step 2 leaves them to the editor. */}
        {currentStep === 1 && draft.fiscalYearsError && (
          <Alert variant="danger" className="d-flex align-items-center justify-content-between">
            <span><AlertCircle size={16} className="me-2" />{draft.fiscalYearsError}</span>
            <Button size="sm" variant="outline-danger" onClick={draft.fetchFiscalYears}>Försök igen</Button>
          </Alert>
        )}
        {currentStep === 1 && draft.error && (
          <Alert variant="danger" onClose={() => draft.setError(null)} dismissible>
            <AlertCircle size={16} className="me-2" />{draft.error}
          </Alert>
        )}
        {currentStep === 1 && draft.roleLegMissing && (
          <Alert variant="warning" className="d-flex align-items-center justify-content-between">
            <span>
              <AlertCircle size={16} className="me-2" />
              {draft.neededRole === 'tax_account'
                ? 'Rollen Skattekonto är inte mappad i din kontoplan — betalkontoraden kan inte förifyllas.'
                : 'Rollen Företagskonto är inte mappad i din kontoplan — betalkontoraden kan inte förifyllas.'}
            </span>
            <Button size="sm" variant="outline-secondary" href={roleSettingsPath(companyId)}>Mappa rollen</Button>
          </Alert>
        )}

        {/* Step 1: choose a source. The choice itself is the navigation. */}
        {currentStep === 1 && (
          <div>
            <div className="mb-3">
              <h6 style={{ fontSize: '1rem', fontWeight: '600', color: '#1f2937' }}>
                <Lightbulb size={18} className="me-2" style={{ color: '#f59e0b' }} />
                Hur vill du bokföra transaktionen?
              </h6>
              <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
                Transaktionen och beloppet är redan kända. Välj ett förslag, sök efter en mall eller ange kontona själv.
              </p>
              <div className="row g-2 mb-3">
                <div className="col-md-4">
                  <Button
                    className="w-100 h-100 text-start p-3"
                    variant="outline-secondary"
                    onClick={() => setDiscoveryMode('suggestions')}
                    style={discoveryMode === 'suggestions' ? { background: '#f1f5f9', borderColor: '#64748b', color: '#1e293b' } : {}}
                  >
                    <strong className="d-block">LedgerLoops förslag</strong>
                    <small>Utifrån transaktionen och tidigare mönster</small>
                  </Button>
                </div>
                <div className="col-md-4">
                  <Button
                    className="w-100 h-100 text-start p-3"
                    variant="outline-secondary"
                    onClick={() => setDiscoveryMode('templates')}
                    style={discoveryMode === 'templates' ? { background: '#f1f5f9', borderColor: '#64748b', color: '#1e293b' } : {}}
                  >
                    <strong className="d-block">Sök bokföringsmall</strong>
                    <small>Bläddra i hela mallbiblioteket</small>
                  </Button>
                </div>
                <div className="col-md-4">
                  <Button className="w-100 h-100 text-start p-3" variant="outline-secondary" onClick={handleSkipSuggestions}>
                    <strong className="d-block">Bokför manuellt</strong>
                    <small>Välj konton och kontering själv</small>
                  </Button>
                </div>
              </div>
            </div>

            {discoveryMode === 'suggestions' && draft.suggestionsLoading && (
              <div className="text-center py-4">
                <Spinner animation="border" variant="primary" />
                <div className="mt-2" style={{ fontSize: '0.875rem', color: '#6b7280' }}>Analyserar transaktion...</div>
              </div>
            )}

            {discoveryMode === 'suggestions' && !draft.suggestionsLoading && draft.suggestions.length > 0 && (
              <div>
                {draft.suggestions.map((suggestion, index) => (
                  <Card
                    key={suggestion.proposal_id || index}
                    className="mb-2"
                    style={{
                      border: draft.proposal?.proposal_id === suggestion.proposal_id ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                    onClick={() => handleSuggestionSelect(suggestion)}
                  >
                    <Card.Body className="p-3">
                      <div className="d-flex align-items-start justify-content-between gap-3">
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <strong style={{ fontSize: '0.875rem', color: '#1f2937' }}>{bookingSuggestionTitle(suggestion)}</strong>
                            {Number.isFinite(suggestion.confidence) && (
                              <Badge bg={getConfidenceBadgeColor(suggestion.confidence)}>{getConfidenceLabel(suggestion)}</Badge>
                            )}
                            {suggestion.strategy && (
                              <Badge bg="secondary" style={{ fontSize: '0.625rem' }}>
                                {suggestion.strategy === 'historical' || suggestion.strategy === 'history' ? 'Historik'
                                  : suggestion.strategy === 'keyword' ? 'Nyckelord'
                                    : suggestion.strategy === 'rule' ? 'Regel'
                                      : suggestion.strategy === 'embedding' ? 'Liknande bokföring'
                                        : suggestion.strategy === 'template' ? 'Mall' : 'Standard'}
                              </Badge>
                            )}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                            {suggestion.reason || 'Förslag baserat på tillgänglig information om transaktionen.'}
                          </div>
                          {Array.isArray(suggestion.evidence) && suggestion.evidence.some(evidenceLabel) && (
                            <div className="mt-2" style={{ fontSize: '0.75rem', color: '#475569' }}>
                              Underlag: {suggestion.evidence.map(evidenceLabel).filter(Boolean).slice(0, 2).join(' · ')}
                            </div>
                          )}
                          {Array.isArray(suggestion.rows) && suggestion.rows.length > 0 && (
                            <div className="mt-2 p-2" style={{ fontSize: '0.75rem', color: '#475569', background: '#f8fafc', borderRadius: 6 }}>
                              {suggestion.rows.map((row, rowIndex) => (
                                <div key={`${row.account_number}-${rowIndex}`} className="d-flex justify-content-between gap-3">
                                  <span>{row.account_number}{row.account_name ? ` – ${row.account_name}` : ''}</span>
                                  <span className="text-nowrap">
                                    {(Number(row.debit) || 0) > 0 ? `D ${formatCurrency(row.debit)}` : `K ${formatCurrency(row.credit)}`}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                          {Array.isArray(suggestion.warnings) && suggestion.warnings.length > 0 && (
                            <div className="mt-2" style={{ fontSize: '0.75rem', color: '#92400e' }}>
                              {suggestion.warnings.map(warningLabel).filter(Boolean).join(' · ')}
                            </div>
                          )}
                        </div>
                        <div>
                          {draft.proposal?.proposal_id === suggestion.proposal_id && <CheckCircle size={20} style={{ color: '#3b82f6' }} />}
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            )}

            {discoveryMode === 'suggestions' && !draft.suggestionsLoading && draft.suggestions.length === 0 && (
              <Alert variant="info">
                <AlertCircle size={16} className="me-2" />
                Inga automatiska förslag hittades. Du kan söka bland mallarna eller bokföra manuellt.
              </Alert>
            )}

            {discoveryMode === 'templates' && (
              <div>
                <Form.Control
                  type="search"
                  value={templateSearch}
                  onChange={(event) => setTemplateSearch(event.target.value)}
                  placeholder="Sök bland alla mallar..."
                  className="mb-3"
                />
                {draft.templatesLoading && <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>}
                {draft.templateError && <Alert variant="danger">{draft.templateError}</Alert>}
                {!draft.templatesLoading && !draft.selectedTemplate && visibleTemplates.map((template) => (
                  <Card key={template.template_id} className="mb-2" role="button" onClick={() => draft.selectTemplate(template)}>
                    <Card.Body className="p-3">
                      <strong style={{ fontSize: '0.875rem' }}>{template.template_name}</strong>
                      {template.description && <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{template.description}</div>}
                    </Card.Body>
                  </Card>
                ))}
                {!draft.templatesLoading && !draft.selectedTemplate && visibleTemplates.length === 0 && (
                  <Alert variant="info">Inga mallar matchar sökningen.</Alert>
                )}
                {draft.selectedTemplate && (
                  <Card>
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div>
                          <strong>{draft.selectedTemplate.template_name}</strong>
                          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{draft.selectedTemplate.description}</div>
                        </div>
                        <Button size="sm" variant="link" onClick={() => draft.setSelectedTemplate(null)}>Byt mall</Button>
                      </div>
                      {(draft.selectedTemplate.body?.inputs || []).map((input) => (
                        <Form.Group className="mb-3" key={input.name}>
                          <Form.Label>{input.label || input.name}{input.required ? ' *' : ''}</Form.Label>
                          {input.type === 'account_picker' ? (
                            <Select
                              options={draft.accounts}
                              theme={brandSelectTheme}
                              value={draft.accounts.find((account) => account.value === String(draft.templateInputs[input.name])) || null}
                              onChange={(option) => draft.setTemplateInputs((current) => ({ ...current, [input.name]: option?.value || '' }))}
                              placeholder="Välj konto..."
                              isClearable
                            />
                          ) : input.type === 'select' ? (
                            <Form.Select
                              value={draft.templateInputs[input.name] ?? ''}
                              onChange={(event) => draft.setTemplateInputs((current) => ({ ...current, [input.name]: event.target.value }))}
                            >
                              <option value="">Välj...</option>
                              {(input.options || []).map((option) => {
                                const value = typeof option === 'object' ? option.value : option;
                                const label = typeof option === 'object' ? option.label : option;
                                return <option key={value} value={value}>{label}</option>;
                              })}
                            </Form.Select>
                          ) : input.type === 'boolean' ? (
                            <Form.Check
                              type="checkbox"
                              checked={Boolean(draft.templateInputs[input.name])}
                              onChange={(event) => draft.setTemplateInputs((current) => ({ ...current, [input.name]: event.target.checked }))}
                            />
                          ) : (
                            <Form.Control
                              type={input.type === 'date' ? 'date' : ['currency', 'currency_gross', 'currency_net', 'number', 'integer'].includes(input.type) ? 'number' : 'text'}
                              step={input.type === 'integer' ? '1' : '0.01'}
                              value={draft.templateInputs[input.name] ?? ''}
                              onChange={(event) => draft.setTemplateInputs((current) => ({ ...current, [input.name]: event.target.value }))}
                            />
                          )}
                          {input.help_text && <Form.Text>{input.help_text}</Form.Text>}
                        </Form.Group>
                      ))}
                      <Button onClick={handleApplyTemplate} disabled={draft.templateEvaluating}>
                        {draft.templateEvaluating ? <Spinner animation="border" size="sm" /> : 'Använd mall'}
                      </Button>
                    </Card.Body>
                  </Card>
                )}
              </div>
            )}
          </div>
        )}

        {/* Step 2: the shared editor; the booking action lives in the footer. */}
        {currentStep === 2 && (
          <div>
            <div className="mb-3">
              <h6 style={{ fontSize: '1rem', fontWeight: '600', color: '#1f2937' }}>
                <Edit3 size={18} className="me-2" style={{ color: '#3b82f6' }} />
                {draft.proposal ? 'Granska bokföringen' : 'Bokför manuellt'}
              </h6>
              <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: 0 }}>
                {draft.proposal
                  ? 'Kontrollera förslaget. Du kan ändra konteringsraderna innan du bokför.'
                  : 'Ange konton och belopp. Verifikatet måste balansera innan det kan bokföras.'}
              </p>
            </div>
            <BookingEditor companyId={companyId} draft={draft} />
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0 d-flex align-items-center">
        <Button
          variant="outline-secondary"
          onClick={currentStep === startStep ? onHide : handleBack}
          disabled={draft.creating}
        >
          <ChevronLeft size={16} className="me-1" />
          {currentStep === startStep ? 'Avbryt' : 'Tillbaka'}
        </Button>

        {currentStep === 2 && (
          <Button variant="success" onClick={handleCreateVerification} disabled={!draft.canSubmit} className="ms-auto">
            {draft.creating ? (
              <><Spinner size="sm" className="me-2" />Bokför...</>
            ) : (
              <><CheckCircle size={16} className="me-2" />Bokför verifikat</>
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default CreateVerificationFromTransactionModal;
